import { useState } from 'react';
import { useUpdateProjectSettings } from '../api/projects';

/**
 * Inline affordance shown next to a branch name when the project has
 * no `repo_url` set. Clicking "add" reveals a small form to paste a
 * git URL (SSH or HTTPS); saving via the project PATCH endpoint
 * immediately turns the branch name into a GitHub compare link.
 *
 * Kept inline (not a modal) so the connection between "this branch
 * isn't linkable" and "set the repo URL" is obvious — the prompt
 * appears in the same place the link would.
 */
export default function RepoUrlPrompt({ projectSlug }: { projectSlug: string }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mutation = useUpdateProjectSettings(projectSlug);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[10px] text-sky-700 hover:text-sky-900 underline decoration-dotted"
        title="Set the project's git repo URL so branch names become GitHub compare links"
      >
        add repo URL
      </button>
    );
  }

  async function save(): Promise<void> {
    setErr(null);
    const trimmed = value.trim();
    if (!trimmed) {
      setErr('Paste a git URL or cancel.');
      return;
    }
    try {
      await mutation.mutateAsync({ repo_url: trimmed });
      setOpen(false);
      setValue('');
    } catch (e) {
      setErr((e as Error).message || 'Failed to save.');
    }
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="git@github.com:owner/repo.git"
        className="text-[11px] font-mono border border-slate-300 rounded px-1.5 py-0.5 w-72"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') {
            setOpen(false);
            setValue('');
            setErr(null);
          }
        }}
      />
      <button
        type="button"
        onClick={() => void save()}
        disabled={mutation.isPending}
        className="text-[11px] px-2 py-0.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
      >
        {mutation.isPending ? '…' : 'save'}
      </button>
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          setValue('');
          setErr(null);
        }}
        className="text-[11px] text-slate-500 hover:text-slate-700"
      >
        cancel
      </button>
      {err && <span className="text-[10px] text-red-600">{err}</span>}
    </span>
  );
}
